import React, { ChangeEvent, ReactNode, useCallback, useMemo } from 'react'

import { SelectChangeEvent } from '@mui/material'
import { useField } from 'formik'

import { FieldSettings, FormHelpersProviderReturn, Option } from '../../../ana-toolbox'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { combinedFields } from '../../utils/yup/main'
import { SelectInput } from '../select-input/select-input'

export type SelectFieldProps = {
  className?: string
  error?: string
  name: string
  label: ReactNode
  required?: boolean
  helperText?: ReactNode
  options: Option[]
}

export const SelectField = (props: SelectFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: SelectChangeEvent<unknown>) => {
      fieldSettings?.hooks?.beforeChange?.(
        e.target.value as string,
        formHelpers as FormHelpersProviderReturn,
        e as ChangeEvent<HTMLInputElement>
      )
      field.onChange(e)
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return <SelectInput {...props} {...field} error={hasError ? meta.error : undefined} onChange={handleChange} />
}
